import 'ol/ol.css';
import Map from 'ol/Map';
import Static from 'ol/source/ImageStatic';
import {Image as ImageLayer} from 'ol/layer';
import {transformExtent, transform} from 'ol/proj';
import LocalScene from '../projections';
import { AerialMap } from './map';

class OrthoImageLayer extends ImageLayer {
  constructor(opts) {
    super(opts);
  }
}

class OrthoMosaic {
  public id;
  private layer: ImageLayer;
  private corners = [];
  private scaleX: number;
  private scaleY: number;
  private imageBase64: string;
  private name: string;
  private imageSize: [Number, Number];
  private imageSizeOriginal: [Number, Number];
  private imagePath: string;
  private fromJSON: boolean;
  private extent = [];
  private orthosList: OrthoMosaicList;
  private pixelMaxSpread = 10;
  private pixelMaxValue = 245;


  constructor(orthosList: OrthoMosaicList, data) {
    this.orthosList = orthosList;
    this.id = data.id;
    this.name = data.name;
    this.corners = data.corners;
    this.imageBase64 = data.base64;
    this.imageSize = data.imageSize;
    this.imageSizeOriginal = data.imageSizeOriginal;
    this.fromJSON = data.fromJSON;
    this.imagePath = data.imagePath;
    this.scaleX = data.scaleX;
    this.scaleY = data.scaleY;

    this.calculateExtent();
    this.addMapLayer();
  }

  get opacity() {
    return this.orthosList.opacity;
  }

  get aerialMap() {
    return this.orthosList.aerialMap;
  }

  get map() {
    return this.orthosList.map;
  }

  get projection() {
    return LocalScene.dataProjectionName;
  }

  get visible() {
    return this.layer.getVisible();
  }

  get path() {
    return this.imagePath;
  }

  get cornerCoordinates() {
    return this.corners;
  }

  get scale() {
    return [this.scaleX, this.scaleY];
  }

  get originalSize() {
    return this.imageSizeOriginal;
  }

  get orthoName() {
    return this.name;
  }

  get metadata() {
    return {
      x_scale: this.scaleX,
      y_scale: this.scaleY,
      x_offset: this.corners[0],
      y_offset: this.corners[3],
      x_skew: 0.0,
      y_skew: 0.0
    };
  }

  zoomToExtent() {
    this.aerialMap.setMapExtent(this.extent);
  }

  calculateExtent() {
    const extent = transformExtent(this.corners,
      this.projection, 'EPSG:3857');
    this.extent = extent;
  }

  transform(coordinates) {
    let coordinatesTransformed = [];
    coordinates.forEach(coordinate =>{
      let coordTransformed = transform(coordinate, 'EPSG:3857', this.projection);
      coordinatesTransformed.push(coordTransformed);
    });
    return coordinatesTransformed;
  }

  reverseTransform(coordinates) {
    let coordinatesTransformed = [];
    coordinates.forEach(coordinate =>{
      let coordTransformed = transform(coordinate, this.projection, 'EPSG:3857');
      coordinatesTransformed.push(coordTransformed);
    });
    return coordinatesTransformed;
  }

  addMapLayer() {
    // Add layer immediately to map
    this.layer = new OrthoImageLayer({opacity: this.opacity});
    this.map.addLayer(this.layer);

    let image = new Image();
    image.onload = () => {
      this.parseImageData(image);
    };
    image.src = this.imageBase64;
  }

  /** @private */
  parseImageData(image) {
    // Grab pixel data from image canvas
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;

    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0);

    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Set white-ish pixels to transparent
    for (var i=0; i<data.length/4; i++) {
      const base = i*4;
      let r = data[base];
      let g = data[base+1];
      let b = data[base+2];

      // Artifical white points will have very little spread
      // between rgb values, even after compression
      let pixel_spread = Math.max(r,g,b) - Math.min(r,g,b);
      if (pixel_spread > this.pixelMaxSpread) {
        continue;
      }

      // Make sure the mean is within a certain range
      let pixel_mean = (r + g + b) / 3;
      let visible = pixel_mean < this.pixelMaxValue;
      data[base + 3] = visible ? 255 : 0;
    }

    context.putImageData(imageData, 0, 0);

    let source = new Static({
      url: canvas.toDataURL("image/png"),
      imageExtent: this.corners,
      projection: this.projection,
      imageSmoothing: true
    });

    this.layer.setSource(source);

    if (!this.fromJSON) {
      this.zoomToExtent();
    }
  }

  setOpacity() {
    this.layer.setOpacity(this.opacity);
  }

  setVisibility(visible) {
    this.layer.setVisible(visible);
  }

  destroy() {
    if (!this.layer) {
      return;
    }

    this.map.removeLayer(this.layer);
    this.imageBase64 = null;
  }
}

export class OrthoMosaicList {
  public orthos: OrthoMosaic[] = [];
  public opacity = 0.8;
  public aerialMap: AerialMap;

  constructor(aerialMap: AerialMap) {
    this.aerialMap = aerialMap;
  }

  get map(): Map {
    return this.aerialMap.map;
  }

  addNewOrtho(data) {
    let cameraID = data.id;
    if (this.getByID(cameraID)) {
      // Ortho already exists
      return false;
    }

    let ortho = new OrthoMosaic(this, data);
    this.orthos.push(ortho);
  }

  deleteOrtho(orthoID) {
    let ortho = this.getByID(orthoID);
    if (!ortho) {
      return;
    }

    ortho.destroy();
    const index = this.orthos.indexOf(ortho);
    this.orthos.splice(index, 1);
  }

  getByID(orthoID) {
    let orthos = this.orthos.filter(ortho => ortho.id === orthoID);
    return orthos.length === 1 ? orthos[0] : null;
  }

  toggleVisibility = (orthoID, visible) => {
    let ortho = this.getByID(orthoID);
    if (!ortho) {
      return;
    }

    ortho.setVisibility(visible);
  }

  setOpacity = (value) => {
    if ((value < 0.0) || (value > 1.0)) {
      return;
    }

    this.opacity = value;
    this.orthos.forEach(ortho => ortho.setOpacity());
  }
}
